import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiHandler from '../services/apiServices';
import { useNavigate } from '@tanstack/react-router';
import axios from 'axios';
  
export interface Lead {
  _id: string;
  org_id: string;
  keywords?: string[];
  notes?: Array<{
    title: string;
    description: string;
    status: string;
    date: string;
  }>;
  company_data: {
    name: string;
    platforms: string[];
    website: string;
    company_website_data: {
      domain: string;
      text: string;
    };
    social_media_company_data: Array<{
      type: string;
      url: string;
      description: string;
      logo: string;
      employees_count: number | null;
      industries: string;
      seniority_level: string;
      job_function: string;
      location: string;
    }>;
    people: Array<{
      first_name: string;
      last_name: string;
      designation: string;
      url: string;
      email: string | null;
      email_status?: "unfetched" | "fetching" | "fetched";
      is_verified: boolean | null;
      profile_pic_url: string | null;
      op: any;
    }>;
    people_fetched?: boolean;
    research_resources?: Array<{
      title: string;
      description: string;
      source: string;
    }>;
    user_feedback?: number | null;
    feedback?: string[] | null;
  };
  signals: Array<{
    intent_lead_id: string;
    intent_signal_id: string;
    signal: string;
    social_media_entities: Array<{
      type: string;
      url: string;
      title: string;
      description: string;
      created_at?: string;
    }>;
    alignment: boolean;
    alignment_reasoning: string;
    alignment_score: string;
  }>;
  people_fetched?: boolean;
  name: string;
  signal: string;
  social_media_company_data?: Array<{
    industries?: string;
    location?: string;
  }>;
  company_website_data?: {
    domain?: string;
  };
  description?: string;
}

interface LeadsResponse {
  data: Lead[];
  total: number;
}

export const useLeads = (itemsPerPage: number, sortOption: string = 'created_at__desc', intentSignalId?: string) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch
  } = useInfiniteQuery<LeadsResponse>({
    queryKey: ['leads', sortOption, intentSignalId],
    queryFn: async ({ pageParam }) => {
      try {
        const skip = (pageParam as number) * itemsPerPage;
        const baseUrl = '/intent-leads/clubbed/';
        const queryParams = new URLSearchParams({
          skip: skip.toString(),
          limit: itemsPerPage.toString(),
          sort: sortOption
        });
        
        if (intentSignalId) {
          queryParams.append('intent_signal_id', intentSignalId);
        }

        const response = await apiHandler.get(
          `${baseUrl}?${queryParams.toString()}`
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.length * itemsPerPage;
      return totalFetched < lastPage.total ? allPages.length : undefined;
    },
    initialPageParam: 0
  });

  const leads = data?.pages.flatMap(page => page.data) || [];
  const totalLeads = data?.pages[0]?.total || 0;

  const fetchFreshData = async () => {
    try {
      const currentQueryData = queryClient.getQueryData(['leads']) as any;
      if (!currentQueryData?.pages?.length) return null;

      const totalFetched = currentQueryData.pages.reduce((acc: number, page: any) => 
        acc + page.data.length, 0);

      const response = await apiHandler.get(`/intent-leads/clubbed/?skip=0&limit=${totalFetched}`);
      
      // Update query cache with fresh data
      queryClient.setQueryData(['leads'], {
        pages: [{
          data: response.data.data,
          total: response.data.total
        }]
      });

      return response.data.data;
    } catch (error) {
      console.error('Error fetching fresh data:', error);
      return null;
    }
  };

  return {
    leads,
    totalLeads,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    fetchFreshData
  };
}; 